import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner, Button, Chip, FormRow } from '../../../ui/index.js';
import type {
  CargoProblemAttachmentError,
  CargoProblemSelectedAttachment,
} from '../useCargoProblemAttachments.js';

export function CargoProblemAttachmentField({
  items,
  busy,
  onAdd,
  onRemove,
}: {
  items: CargoProblemSelectedAttachment[];
  busy: boolean;
  onAdd: (files: readonly File[]) => CargoProblemAttachmentError | null;
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation('cargoProblems');
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<CargoProblemAttachmentError | null>(null);

  function choose(files: FileList | null) {
    if (!files || busy) return;
    setError(onAdd(Array.from(files)));
  }

  return (
    <FormRow label={t('editor.attachments')}>
      <input
        ref={inputRef}
        className="cargo-problem-file-input"
        type="file"
        multiple
        disabled={busy}
        tabIndex={-1}
        aria-label={t('files.attach')}
        onChange={(event) => {
          choose(event.target.files);
          event.target.value = '';
        }}
      />
      <div className="cargo-problem-attachment-control">
        <Button icon="paperclip" disabled={busy} onClick={() => inputRef.current?.click()}>
          {t('files.attach')}
        </Button>
        <span>{t('files.selected', { count: items.length })}</span>
      </div>
      {items.length > 0 ? (
        <div className="cargo-problem-attachment-list" aria-label={t('files.selectedList')}>
          {items.map((item) => (
            <Chip
              key={item.id}
              icon={item.error ? 'alert-circle' : 'paperclip'}
              {...(busy
                ? {}
                : {
                    onRemove: () => onRemove(item.id),
                    removeLabel: t('files.remove', { name: item.file.name }),
                  })}
              title={item.file.name}
            >
              <span className="cargo-problem-attachment-name">{item.file.name}</span>
              {item.error ? (
                <span className="cargo-problem-attachment-error" role="alert">
                  {t(`files.errors.${item.error}`)}
                </span>
              ) : null}
            </Chip>
          ))}
        </div>
      ) : null}
      {error ? <Banner tone="error">{t(`files.errors.${error}`)}</Banner> : null}
    </FormRow>
  );
}
